import React, { useState, useEffect } from "react";
import { BsPlusSquareFill } from "react-icons/bs";
import { ImCross } from "react-icons/im";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useCurrency } from '../../../../context/currencyContext';
import Swal from 'sweetalert2';

function CloseInvoiceCase({ caseData, onInvoiceCreated }) {
  const { formatAmount } = useCurrency();
  
  // Form state
  const [trackNumber, setTrackNumber] = useState("");
  const [shippingCost, setShippingCost] = useState("0.00");
  const [rescheduleOption, setRescheduleOption] = useState("Ship Today");
  const [invoiceNow, setInvoiceNow] = useState(true);
  const [relatedCase, setRelatedCase] = useState(false);
  const [addShippingCost, setAddShippingCost] = useState(false);
  const [invoiceMessage, setInvoiceMessage] = useState("Thank you for your business!");
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [status, setStatus] = useState("unpaid");
  
  // Case and user info
  const { caseId } = useParams();
  const [userLabId, setUserLabId] = useState("");

  // Procedure units and amounts (editable)
  const [units, setUnits] = useState([]);
  const [amounts, setAmounts] = useState([]);
  
  // Totals
  const [subtotal, setSubtotal] = useState(0);
  const [taxes] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Additional invoice line items
  const [items, setItems] = useState([]);
  
  // Modal visibility
  const [showModal, setShowModal] = useState(false);
  
  // Shipping options from backend
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(true);
  
  // Flag to track if shipping cost was manually changed
  const [isShippingCostManuallySet, setIsShippingCostManuallySet] = useState(false);

  // Load userLabId from localStorage on mount
  useEffect(() => {
    const storedUserLabId = localStorage.getItem('userLabId');
    if (storedUserLabId) {
      setUserLabId(storedUserLabId);
    }
  }, []);

  // Initialize units and amounts from case procedures
  useEffect(() => {
    if (caseData?.procedures && Array.isArray(caseData.procedures)) {
      const initialUnits = caseData.procedures.map(proc => proc.calculatedUnits || 1);
      const initialAmounts = caseData.procedures.map((proc, i) => {
        const basePrice = Number(proc.price) / (proc.calculatedUnits || 1);
        return initialUnits[i] * basePrice;
      });
      setUnits(initialUnits);
      setAmounts(initialAmounts);
    } else {
      setUnits([]);
      setAmounts([]);
    }
  }, [caseData]);

  // Fetch case shipping data to auto-populate fields
  const fetchCaseShippingData = async () => {
    try {
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId || caseData?.caseNumber}`
      );
      
      const caseDetails = response.data;
      
      if (caseDetails?.shipmentTrackingUrls && caseDetails.shipmentTrackingUrls.length > 0) {
        setTrackNumber(caseDetails.shipmentTrackingUrls[0]);
      } else if (caseDetails?.trackNumber) {
        setTrackNumber(caseDetails.trackNumber);
      }
      
      if (caseDetails?.shippingCost) {
        const costValue = Number(caseDetails.shippingCost).toFixed(2);
        setShippingCost(costValue);
        setAddShippingCost(Number(caseDetails.shippingCost) > 0);
        setIsShippingCostManuallySet(true);
      }
      
      if (caseDetails?.delivery_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deliveryDate = new Date(caseDetails.delivery_date);
        deliveryDate.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        if (deliveryDate.getTime() === today.getTime()) {
          setRescheduleOption("Ship Today");
        } else if (deliveryDate.getTime() === tomorrow.getTime()) {
          setRescheduleOption("Ship Next Business Day");
        } else {
          const formattedDate = deliveryDate.toISOString().split("T")[0];
          setRescheduleOption(formattedDate);
        }
      }
      
      return caseDetails;
    } catch (error) {
      console.error("Error fetching case shipping data:", error);
      return null;
    }
  };

  // Fetch available shipping services from backend filtered by userLabId
  const fetchShippingTypes = async (caseShipperInfo = null) => {
    setShippingLoading(true);
    try {
      const storedUserLabId = localStorage.getItem('userLabId');
      
      const res = await axios.get("https://hms.backend.hiragroupcloud.com/api/shipping", {
        params: {
          addedBy: storedUserLabId
        }
      });
      
      if (res.data.success) {
        let options = res.data.data
          .map(st => ({
            id: st._id,
            label: st.service ? `${st.company} - ${st.service}` : st.company,
            price: Number(st.price) || 0,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        let selected = null;
        let price = 0;

        const shipperLabel = caseShipperInfo?.shipper || 
                           caseData?.shipper || 
                           (caseData?.shippingService?.company && caseData?.shippingService?.service 
                             ? `${caseData.shippingService.company} - ${caseData.shippingService.service}`
                             : caseData?.shippingService?.company || caseData?.shipper);
        
        if (shipperLabel && shipperLabel !== "No Shipper") {
          const assignedLabel = shipperLabel;
          price = caseShipperInfo?.shippingCost || Number(caseData?.shippingCost || caseData?.shippingService?.price || 0);
          
          selected = options.find(o => o.label === assignedLabel);
          
          if (!selected) {
            selected = { id: `custom-${Date.now()}`, label: assignedLabel, price };
            options.push(selected);
          } else if (price > 0) {
            selected = { ...selected, price: price };
          }
        }

        setShippingOptions(options);
        
        if (selected) {
          setSelectedShipping(selected);
          
          if (!isShippingCostManuallySet) {
            setShippingCost(selected.price.toFixed(2));
            setAddShippingCost(selected.price > 0);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching shipping types:", err);
      let selected = null;
      let price = 0;
      let options = [];
      
      const shipperLabel = caseData?.shipper || 
                         (caseData?.shippingService?.company && caseData?.shippingService?.service 
                           ? `${caseData.shippingService.company} - ${caseData.shippingService.service}`
                           : caseData?.shippingService?.company || caseData?.shipper);
      
      if (shipperLabel && shipperLabel !== "No Shipper") {
        const label = shipperLabel;
        price = Number(caseData?.shippingCost || caseData?.shippingService?.price || 0);
        selected = { id: "fallback-custom", label, price };
        options = [selected];
      }
      
      setShippingOptions(options);
      
      if (selected) {
        setSelectedShipping(selected);
        
        if (!isShippingCostManuallySet) {
          setShippingCost(selected.price.toFixed(2));
          setAddShippingCost(selected.price > 0);
        }
      }
    } finally {
      setShippingLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setIsShippingCostManuallySet(false);
      const caseDetails = await fetchCaseShippingData();
      await fetchShippingTypes(caseDetails);
    };
    
    initializeData();
  }, [caseData]);

  // Recalculate subtotal and total whenever relevant values change
  useEffect(() => {
    const procedureTotal = amounts.reduce((sum, a) => sum + (Number(a) || 0), 0);
    const extraTotal = items.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.amount) || 0), 0);
    const shipping = addShippingCost ? Number(shippingCost) || 0 : 0;
    const newSubtotal = procedureTotal + extraTotal + shipping;
    const newTotal = newSubtotal + taxes;
    setSubtotal(newSubtotal);
    setTotal(newTotal);
  }, [amounts, items, addShippingCost, shippingCost, taxes]);

  // Format delivery date for reschedule option
  const deliveryDateOption = caseData?.delivery_date
    ? new Date(caseData.delivery_date).toISOString().split("T")[0]
    : null;

  // Add a new empty additional line item
  const handleAddItem = () => {
    setItems([...items, { quantity: 1, amount: 0, description: "" }]);
  };

  // Update units for a procedure → recalculate amount
  const handleUnitChange = (index, value) => {
    const newUnits = [...units];
    const newAmounts = [...amounts];
    const unitCount = Math.max(1, Number(value) || 1);
    newUnits[index] = unitCount;
    const basePrice = Number(caseData.procedures[index].price) / (caseData.procedures[index].calculatedUnits || 1);
    newAmounts[index] = unitCount * basePrice;
    setUnits(newUnits);
    setAmounts(newAmounts);
  };

  // Direct amount override for a procedure
  const handleAmountChange = (index, value) => {
    const newAmounts = [...amounts];
    newAmounts[index] = Math.max(0, Number(value) || 0);
    setAmounts(newAmounts);
  };

  // Update additional line item fields
  const handleFieldChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = field === "quantity" || field === "amount" ? Math.max(0, Number(value) || 0) : value;
    setItems(updated);
  };

  // Remove an additional line item
  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Open modal only if user is logged in
  const handleShow = async () => {
    const storedUserLabId = localStorage.getItem('userLabId');
    if (!storedUserLabId) {
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: 'Please log in to create an invoice',
      });
      return;
    }
    setUserLabId(storedUserLabId);
    
    setIsShippingCostManuallySet(false);
    
    try {
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId || caseData?.caseNumber}`
      );
      
      const latestCaseData = response.data;
      
      if (latestCaseData?.shipmentTrackingUrls && latestCaseData.shipmentTrackingUrls.length > 0) {
        setTrackNumber(latestCaseData.shipmentTrackingUrls[0]);
      } else if (latestCaseData?.trackNumber) {
        setTrackNumber(latestCaseData.trackNumber);
      }
      
      if (latestCaseData?.shippingCost) {
        const costValue = Number(latestCaseData.shippingCost).toFixed(2);
        setShippingCost(costValue);
        setAddShippingCost(Number(latestCaseData.shippingCost) > 0);
        setIsShippingCostManuallySet(true);
      }
      
      if (latestCaseData?.delivery_date) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const deliveryDate = new Date(latestCaseData.delivery_date);
        deliveryDate.setHours(0, 0, 0, 0);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        if (deliveryDate.getTime() === today.getTime()) {
          setRescheduleOption("Ship Today");
        } else if (deliveryDate.getTime() === tomorrow.getTime()) {
          setRescheduleOption("Ship Next Business Day");
        } else {
          const formattedDate = deliveryDate.toISOString().split("T")[0];
          setRescheduleOption(formattedDate);
        }
      }
      
      if (latestCaseData?.shipper && latestCaseData.shipper !== "No Shipper") {
        await fetchShippingTypes({
          shipper: latestCaseData.shipper,
          shippingCost: latestCaseData.shippingCost
        });
      }
    } catch (error) {
      console.error("Error fetching latest case data:", error);
    }
    
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  // Handle shipping service selection
  const handleShipperChange = (e) => {
    const label = e.target.value;
    if (label === "") {
      setSelectedShipping(null);
      setShippingCost("0.00");
      setAddShippingCost(false);
      setIsShippingCostManuallySet(false);
    } else {
      const found = shippingOptions.find(o => o.label === label);
      if (found) {
        setSelectedShipping(found);
        setShippingCost(found.price.toFixed(2));
        setAddShippingCost(found.price > 0);
        setIsShippingCostManuallySet(false);
      }
    }
  };

  // Handle manual shipping cost change
  const handleShippingCostChange = (value) => {
    setShippingCost(value || "0.00");
    setIsShippingCostManuallySet(true);
  };

  // Helper function to get account email
  const getAccountEmail = (account) => {
    if (account?.invoice_email && account.invoice_email.trim()) {
      return account.invoice_email;
    }
    if (account?.email && account.email.trim()) {
      return account.email;
    }
    return null;
  };

// Helper function to send invoice email
  const sendInvoiceEmail = async (invoiceId, accountEmail, accountName, invoiceData) => {
    try {
      const storedUserLabId = localStorage.getItem('userLabId');

      // Get user's email configuration
      // FIX: correct endpoint is /api/statements/user-email, and since it's a GET
      // request we must pass userLabId as a query param (not a body field).
      const userRes = await axios.get(`https://hms.backend.hiragroupcloud.com/api/statements/user-email`, {
        params: { userLabId: storedUserLabId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const userEmail = userRes.data?.statementEmail || userRes.data?.fallbackEmail;

      // Prepare email data
      const emailData = {
        to: accountEmail,
        subject: `Invoice #${invoiceData.invoiceNumber || invoiceId} from ${userRes.data?.labName || 'Hira Dental Lab'}`,
        invoiceId: invoiceId,
        invoiceData: invoiceData,
        userEmail: userEmail,
        labName: userRes.data?.labName || 'Hira Dental Lab',
        userLabId: storedUserLabId // FIX: backend requires this in req.body
      };

      // Send email through backend
      // FIX: correct endpoint is /api/statements/send-invoice-email
      // (this route lives on the statements router, not an /api/invoices router)
      const response = await axios.post(
        `https://hms.backend.hiragroupcloud.com/api/statements/send-invoice-email`,
        emailData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error sending invoice email:', error);
      throw error;
    }
  };
  // Save invoice to backend
  const handleSave = async (sendEmail = false) => {
    if (!userLabId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to create invoice: User session not found. Please log in again.',
      });
      return;
    }

    try {
      const procedureItems = caseData.procedures.map((proc, i) => ({
        description: proc.procedure,
        quantity: Number(units[i]) || 1,
        amount: Number(amounts[i]) || 0,
      }));

      const shippingItem = addShippingCost
        ? [{ description: "Shipping Cost", quantity: 1, amount: Number(shippingCost) || 0 }]
        : [];

      // Generate invoice number
      const invoiceNumber = `INV-${Date.now()}`;

      const invoiceData = {
        caseId: caseData?.caseNumber || caseId,
        accountId: caseData?.account_holder || caseId,
        addedBy: userLabId,
        trackNumber,
        shippingCost: addShippingCost ? parseFloat(shippingCost) || 0 : 0,
        shipper: selectedShipping?.label || "",
        rescheduleOption,
        invoiceNow,
        relatedCase,
        addShippingCost,
        items: [
          ...procedureItems,
          ...items.map(item => ({
            description: item.description,
            quantity: Number(item.quantity) || 1,
            amount: Number(item.amount) || 0,
          })),
          ...shippingItem,
        ],
        subtotal: Number(subtotal) || 0,
        taxes: Number(taxes) || 0,
        total: Number(total) || 0,
        invoiceDate,
        invoiceMessage,
        printAfterSave,
        status,
        invoiceNumber: invoiceNumber,
      };
      
      const res = await axios.post("https://hms.backend.hiragroupcloud.com/api/invoices/invoices", invoiceData);
      
      // Get the created invoice data
      const createdInvoice = res.data;
      onInvoiceCreated?.(createdInvoice);

// If sendEmail is true, try to send the invoice via email
      if (sendEmail) {
        try {
          // Get account email from case data
          const accountId = caseData?.account_holder || caseId;
          
          // Fetch account details to get email
          const accountResponse = await axios.get(
            `https://hms.backend.hiragroupcloud.com/api/account/account/${accountId}`
          );
          
          const account = accountResponse.data;
          const accountEmail = getAccountEmail(account);
          
          if (accountEmail) {
            // FIX: show the loading indicator WITHOUT awaiting it.
            // Awaiting Swal.fire() here blocks forever because nothing closes
            // the modal — that's why the UI just sat on "Sending Invoice..."
            Swal.fire({
              title: 'Sending Invoice...',
              text: 'Please wait while we send the invoice to the account email.',
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              }
            });

            // Send the invoice email
            await sendInvoiceEmail(
              createdInvoice._id || createdInvoice.invoiceNumber,
              accountEmail,
              account.account_name || account.accountId,
              { ...invoiceData, _id: createdInvoice._id }
            );

            // FIX: close the loading modal before showing the result
            Swal.close();

            // Success - both saved and sent
            await Swal.fire({
              icon: 'success',
              title: 'Success!',
              text: `Invoice created and sent successfully to ${accountEmail}!`,
              timer: 3000,
              showConfirmButton: false
            });
          } else {
            // Account has no email
            await Swal.fire({
              icon: 'warning',
              title: 'Invoice Created',
              text: 'Invoice was created but no email address found for this account. Please add an email to the account to send invoices.',
              timer: 3000,
              showConfirmButton: false
            });
          }
        } catch (emailError) {
          console.error('Error sending invoice email:', emailError);

          // FIX: make sure the loading modal is closed even on failure
          Swal.close();

          // Show warning but invoice was already created
          await Swal.fire({
            icon: 'warning',
            title: 'Invoice Created',
            text: `Invoice was created but failed to send via email: ${emailError.response?.data?.message || emailError.message}`,
            confirmButtonText: 'OK'
          });
        }
      } else {
        // Just saved, no email
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Invoice created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      }

      handleClose();
    } catch (error) {
      // Error SweetAlert
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to create invoice',
      });
    }
  };

  return (
    <div>
      <button
        style={{
          padding: "7px 10px",
          fontSize: "16px",
          cursor: "pointer",
          backgroundColor: "#004aad",
          color: "white",
          border: "none",
          borderRadius: "5px",
          marginBottom: "2px",
        }}
        onClick={handleShow}
      >
        Close/Invoice Case
      </button>

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "750px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#004aad",
                padding: "10px 15px",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                borderTopLeftRadius: "5px",
                borderTopRightRadius: "5px",
                margin: "-20px -20px 20px -20px",
              }}
            >
              <span>Close Case</span>
              <button
                style={{ background: "transparent", color: "white", border: "none", fontSize: "20px", cursor: "pointer" }}
                onClick={handleClose}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "0 20px 20px" }}>
              {/* Shipping Details Section */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px 0" }}>Shipping Details</h3>
              <div style={{ backgroundColor: "#f4f4f4", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px", marginBottom: "30px" }}>
                {["Reschedule", "Shipper", "Track #", "Shipping Cost"].map((label) => {
                  const isShipper = label === "Shipper";
                  const isShippingCost = label === "Shipping Cost";
                  return (
                    <div key={label} style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                      <label style={{ fontWeight: "600", width: "150px", color: "#333" }}>{label}</label>
                      {label === "Reschedule" ? (
                        <select value={rescheduleOption} onChange={e => setRescheduleOption(e.target.value)} style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}>
                          <option>Ship Today</option>
                          <option>Ship Next Business Day</option>
                          {deliveryDateOption && <option value={deliveryDateOption}>Delivery Date ({deliveryDateOption})</option>}
                        </select>
                      ) : isShipper ? (
                        <select
                          value={selectedShipping?.label || ""}
                          onChange={handleShipperChange}
                          disabled={shippingLoading}
                          style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
                        >
                          <option value="">-- Select Shipping Service --</option>
                          {shippingLoading ? (
                            <option>Loading shipping options...</option>
                          ) : shippingOptions.length === 0 ? (
                            <option>No shipping options available</option>
                          ) : (
                            shippingOptions.map(opt => (
                              <option key={opt.id} value={opt.label}>
                                {opt.label} {opt.price > 0 ? `(${formatAmount(opt.price)})` : ''}
                              </option>
                            ))
                          )}
                        </select>
                      ) : isShippingCost ? (
                        <div style={{ width: "40%", display: "flex", flexDirection: "column" }}>
                          <input
                            type="number"
                            value={shippingCost}
                            onChange={e => handleShippingCostChange(e.target.value)}
                            min="0"
                            step="0.01"
                            style={{ width: "100%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
                          />
                          {selectedShipping && selectedShipping.price > 0 && !isShippingCostManuallySet && (
                            <small style={{ color: "#004aad", marginTop: "4px", fontSize: "12px" }}>
                              Auto-filled from selected service: {formatAmount(selectedShipping.price)}
                            </small>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={trackNumber}
                          onChange={e => setTrackNumber(e.target.value)}
                          style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Additional Actions */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px 0" }}>Additional Actions</h3>
              <div style={{ backgroundColor: "#f4f4f4", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px", marginBottom: "30px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
                  <label><input type="checkbox" checked={invoiceNow} onChange={() => setInvoiceNow(!invoiceNow)} /> <strong>Invoice Now</strong></label>
                  <label><input type="checkbox" checked={relatedCase} onChange={() => setRelatedCase(!relatedCase)} /> <strong>Related Case</strong></label>
                </div>
                <label><input type="checkbox" checked={addShippingCost} onChange={() => setAddShippingCost(!addShippingCost)} /> <strong>Add Shipping Cost to Invoice</strong></label>
              </div>

              {/* Invoice Details */}
              <h3 style={{ color: "#1E90FF", fontSize: "20px", padding: "10px 0" }}>Invoice Details</h3>
              <div style={{ backgroundColor: "white", padding: "15px", borderTop: "2px solid #004aad", borderRadius: "8px" }}>
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ fontWeight: "600", width: "150px", color: "#333" }}>Invoice Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: "40%", padding: "8px", borderRadius: "8px", border: "1px solid #ccc" }}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>

                {/* Procedures Table */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, marginBottom: "20px", boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
                  <thead>
                    <tr>
                      {["Units", "Description", "Amount"].map(h => (
                        <th key={h} style={{ padding: "12px 16px", backgroundColor: "#f4f4f4", color: "black", textAlign: "left", fontWeight: "600", borderBottom: "2px solid #e0e0e0" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(caseData?.procedures || []).map((proc, i) => (
                      <tr key={proc._id || i} style={{ backgroundColor: i % 2 === 0 ? "#f9f9f9" : "#fff" }}>
                        <td style={{ padding: "12px 16px" }}>
                          <input
                            type="number"
                            value={units[i] || proc.calculatedUnits || 1}
                            onChange={e => handleUnitChange(i, e.target.value)}
                            min="1"
                            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "12px 16px" }}>{proc.procedure}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <input
                            type="number"
                            value={amounts[i] || 0}
                            onChange={e => handleAmountChange(i, e.target.value)}
                            min="0"
                            step="0.01"
                            style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Additional Line Items */}
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 10px", marginTop: "20px" }}>
                  <tbody>
                    <tr>
                      <td colSpan="4" style={{ textAlign: "left", fontWeight: "bold", fontSize: "16px", padding: "15px 10px", backgroundColor: "#f1f4f9", borderRadius: "8px" }}>
                        Additional Lines
                        <BsPlusSquareFill
                          style={{
                            color: "#004aad",
                            fontSize: "30px",
                            cursor: "pointer",
                            float: "right",
                            marginTop: "-5px"
                          }}
                          onClick={handleAddItem}
                        />
                      </td>
                    </tr>
                    {items.map((item, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f9f9f9", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", borderRadius: "6px" }}>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="text"
                            value={item.description}
                            onChange={e => handleFieldChange(i, "description", e.target.value)}
                            placeholder="Item description"
                            style={{ padding: "8px", width: "130px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={e => handleFieldChange(i, "quantity", e.target.value)}
                            min="0"
                            placeholder="Qty"
                            style={{ padding: "8px", width: "90px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={e => handleFieldChange(i, "amount", e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="Amount"
                            style={{ padding: "8px", width: "90px", borderRadius: "6px", border: "1px solid #ccc" }}
                          />
                        </td>
                        <td style={{ backgroundColor: "#004aad", borderRadius: "6px", height: "40px", width: "40px", display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }} onClick={() => handleRemoveItem(i)}>
                          <ImCross style={{ color: "white", fontSize: "16px" }} />
                        </td>
                      </tr>
                    ))}
                    {addShippingCost && (
                      <tr style={{ backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
                        <td style={{ padding: "10px" }}>Shipping Cost</td>
                        <td style={{ padding: "10px" }}>1</td>
                        <td style={{ padding: "10px" }}>{formatAmount(Number(shippingCost))}</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Totals */}
                <table style={{ width: "100%", marginTop: "20px" }}>
                  <tbody>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Subtotal</td><td style={{ padding: "10px", textAlign: "right" }}>{formatAmount(subtotal)}</td></tr>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}>Taxes</td><td style={{ padding: "10px", textAlign: "right" }}>{formatAmount(taxes)}</td></tr>
                    <tr><td style={{ padding: "10px", fontWeight: "bold", textAlign: "right" }}><strong>Total</strong></td><td style={{ padding: "10px", textAlign: "right" }}><strong>{formatAmount(total)}</strong></td></tr>
                  </tbody>
                </table>

                <br />
                <div style={{ display: "flex", marginBottom: "15px", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", width: "50%", textAlign: "left" }}>Invoice Date</label>
                  <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} style={{ width: "40%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }} />
                </div>
                <div style={{ display: "flex", marginBottom: "15px", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", width: "50%", textAlign: "left" }}>Invoice Message</label>
                  <input type="text" value={invoiceMessage} onChange={e => setInvoiceMessage(e.target.value)} style={{ width: "40%", padding: "12px", borderRadius: "8px", border: "1px solid #ccc" }} placeholder="Visible to customers" />
                </div>
                <label style={{ display: "block", marginBottom: "20px" }}>
                  <input type="checkbox" checked={printAfterSave} onChange={() => setPrintAfterSave(!printAfterSave)} style={{ marginRight: "10px" }} />
                  Print after invoice is saved?
                </label>

                {/* Action Buttons */}
                <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "20px", gap: "10px" }}>
                  <button 
                    style={{ 
                      padding: "10px 20px", 
                      margin: "5px", 
                      backgroundColor: "#ccc", 
                      color: "#333", 
                      border: "none", 
                      borderRadius: "5px", 
                      cursor: "pointer" 
                    }} 
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button 
                    style={{ 
                      padding: "10px 20px", 
                      margin: "5px", 
                      backgroundColor: "#004aad", 
                      color: "white", 
                      border: "none", 
                      borderRadius: "5px", 
                      cursor: "pointer" 
                    }} 
                    onClick={() => handleSave(false)}
                  >
                    Save Invoice
                  </button>
                  <button 
                    style={{ 
                      padding: "10px 20px", 
                      margin: "5px", 
                      backgroundColor: "#28a745", 
                      color: "white", 
                      border: "none", 
                      borderRadius: "5px", 
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }} 
                    onClick={() => handleSave(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    Save & Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CloseInvoiceCase;